import type { ReactNode } from "react";
import type { AskResult } from "@nl2sparql/core";
import { ResultsTable } from "./ResultsTable.tsx";

export function AnswerCard({ result }: { result: AskResult }) {
  const chosen = result.chosenIndex !== undefined ? result.attempts[result.chosenIndex] : undefined;

  if (!result.ok || !result.answer) {
    return (
      <div
        className="rounded-xl border p-5"
        style={{ borderColor: "var(--nl2s-border)", background: "var(--nl2s-surface)" }}
      >
        <div className="text-sm font-semibold" style={{ color: "var(--nl2s-warn)" }}>
          No answer
        </div>
        <p className="mt-1 text-sm" style={{ color: "var(--nl2s-text-muted)" }}>
          {result.error ?? "The query ran but returned no results. Try rephrasing, or check the linked entities in the Explain panel below."}
        </p>
      </div>
    );
  }

  const a = result.answer;
  const meta = chosen ? (
    <span className="text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
      {chosen.strategy} · {chosen.rowCount} row{chosen.rowCount === 1 ? "" : "s"}
      {chosen.elapsedMs !== undefined ? ` · ${chosen.elapsedMs}ms` : ""} · {result.sourceId}
    </span>
  ) : null;

  let body: ReactNode;
  if (a.kind === "count") {
    body = <div className="text-4xl font-bold" style={{ color: "var(--nl2s-text)" }}>{a.value.toLocaleString()}</div>;
  } else if (a.kind === "boolean") {
    body = (
      <div className="text-3xl font-bold" style={{ color: a.value ? "var(--nl2s-ok)" : "var(--nl2s-danger)" }}>
        {a.value ? "Yes" : "No"}
      </div>
    );
  } else if (a.rows.length === 1 && a.columns.length === 1) {
    const cell = a.rows[0]?.[0];
    body = cell?.iri ? (
      <a href={cell.iri} target="_blank" rel="noreferrer" className="text-3xl font-bold" style={{ color: "var(--nl2s-accent)" }}>
        {cell.display || cell.iri}
      </a>
    ) : (
      <div className="text-3xl font-bold" style={{ color: "var(--nl2s-text)" }}>{cell?.display}</div>
    );
  } else {
    body = (
      <div>
        <div className="mb-2 text-sm font-medium" style={{ color: "var(--nl2s-text-muted)" }}>
          {a.rows.length} result{a.rows.length === 1 ? "" : "s"}
        </div>
        <ResultsTable answer={a} />
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border p-5"
      style={{ borderColor: "var(--nl2s-border)", background: "var(--nl2s-surface)", boxShadow: "var(--nl2s-shadow)" }}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--nl2s-accent)" }}>
          Answer
        </span>
        {meta}
      </div>
      {body}
    </div>
  );
}
